import type React from 'react';
import { useState } from 'react';
import type { DefaultTheme } from 'styled-components';
import { css, styled } from 'styled-components';

import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef/index.ts';

import Typography from '../../Typography/index.ts';
import { CloseButton, InfoIcon, Message } from './common.tsx';
import type { NotificationProps as SharedNotificationProps, NotificationType } from './types.ts';

const getColorStyles = ({ $type, theme }: { $type?: NotificationType; theme: DefaultTheme }) => {
  switch ($type) {
    case 'success':
      return css`
        background-color: ${theme.colors.positive10};
        border: 1px solid ${theme.colors.positive30};
        & ${Typography.TextMedium} {
          color: ${theme.colors.positive120};
        }
      `;
    case 'error':
      return css`
        background-color: ${theme.colors.critical10};
        border: 1px solid ${theme.colors.critical30};
        & ${Typography.TextMedium} {
          color: ${theme.colors.critical120};
        }
      `;
    case 'warning':
      return css`
        background-color: ${theme.colors.warning10};
        border: 1px solid ${theme.colors.warning30};
        & ${Typography.TextMedium} {
          color: ${theme.colors.warning120};
        }
      `;
    default:
      return css`
        background-color: ${theme.colors.link10};
        border: 1px solid ${theme.colors.link30};
        & ${Typography.TextMedium} {
          color: ${theme.colors.link120};
        }
      `;
  }
};

const Wrapper = styled.div<{ $type?: NotificationType; $clickable?: boolean }>`
  min-height: 36px;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  ${({ $clickable }) => $clickable && 'cursor: pointer;'}
  ${getColorStyles}
`;

export type BaseNotificationProps = SharedNotificationProps & {
  maxHeight?: number;
  closeable?: boolean;
  onClose?: () => void;
  onClick?: () => void;
  role?: 'alert';
};

export const BaseNotification: React.FC<BaseNotificationProps> = ({
  type = 'default',
  title,
  body,
  className,
  maxHeight,
  closeable,
  onClose,
  onClick,
  role,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = useCallbackRef((e: React.MouseEvent) => {
    e.stopPropagation();
    setIsVisible(false);
    onClose?.();
  });

  if (!isVisible) return null;

  return (
    <Wrapper $type={type} $clickable={!!onClick} className={className} role={role} onClick={onClick}>
      <InfoIcon type={type} />
      <Message title={title} body={body} type={type} maxHeight={maxHeight} />
      {closeable && <CloseButton closeToast={handleClose} />}
    </Wrapper>
  );
};
